// P3-027: Overload status overlay during battle
// Current overload level display, warning message when fluctuation triggers

import { useEffect, useRef, useState } from 'react';
import { useBattleStore } from '../stores/battleStore';
import { EventBus } from '@/shared/game/EventBus';

/** Overload level display labels */
const LEVEL_LABELS: Record<string, string> = {
  none: 'STABLE',
  light: 'OVERLOAD: LIGHT',
  medium: 'OVERLOAD: MEDIUM',
  heavy: 'OVERLOAD: HEAVY',
};

export function HardwareOverlay() {
  const overloadLevel = useBattleStore((s) => s.overloadLevel);
  const [warningVisible, setWarningVisible] = useState(false);
  const [affectedCount, setAffectedCount] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleOverload = (data: { affectedAgentIds: string[] }) => {
      setAffectedCount(data.affectedAgentIds.length);
      setWarningVisible(true);

      // Clear previous timer to prevent flickering on rapid events
      if (timerRef.current) clearTimeout(timerRef.current);

      timerRef.current = setTimeout(() => {
        setWarningVisible(false);
        timerRef.current = null;
      }, 3000);
    };

    EventBus.on('overloadTriggered', handleOverload);
    return () => {
      EventBus.off('overloadTriggered', handleOverload);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Don't show anything if no overload
  if (overloadLevel === 'none' && !warningVisible) return null;

  return (
    <div className="hardware-overlay">
      {overloadLevel !== 'none' && (
        <div className={`hardware-overlay__level hardware-overlay__level--${overloadLevel}`}>
          {LEVEL_LABELS[overloadLevel] ?? overloadLevel}
        </div>
      )}

      {warningVisible && (
        <div className="hardware-overlay__warning">
          [WARNING] Heat dissipation insufficient, Agent response delayed
          {affectedCount > 0 && ` (${affectedCount} agent${affectedCount > 1 ? 's' : ''} affected)`}
        </div>
      )}
    </div>
  );
}
